/**
 * NUVRYA commerce runtime — Quick View, facets, sorting, variants,
 * sticky purchase, before/after and lightbox navigation.
 *
 * Each behaviour reproduces a measured one. Where the reference measured
 * *worse* than the brief assumed, the measurement wins and the reason is
 * recorded inline.
 *
 * Sources: build/forensic/out/fx-collection.json, fx-product.json, fx-corrections.json
 */

const RM = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Announce a change to assistive tech without stealing focus. */
function announce(message) {
  let region = document.getElementById('nuvrya-live-region');
  if (!region) {
    region = document.createElement('div');
    region.id = 'nuvrya-live-region';
    region.className = 'visually-hidden';
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('role', 'status');
    document.body.appendChild(region);
  }
  region.textContent = message;
}

/* =============================================================
 * Quick View
 *
 * Measured: clicking the card control fetches the product with
 * `?section_id=<quick-view section>` and the modal is usable at 389ms
 * (the request itself is 287ms). The brief's ~750ms target was wrong;
 * building to 750ms would have shipped something visibly slower than
 * the reference.
 * ============================================================= */
class NuvryaQuickView extends HTMLElement {
  #controller = null;
  #cache = new Map();

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const trigger = event.target.closest('[data-nuvrya-quickview-url]');
    if (!trigger) return;
    event.preventDefault();
    this.open(trigger.getAttribute('data-nuvrya-quickview-url'), trigger);
  };

  async open(url, trigger) {
    const dialog = document.getElementById('nuvrya-quick-view');
    if (!dialog) return;

    this.returnFocusTo = trigger ?? document.activeElement;
    const body = dialog.querySelector('[data-nuvrya-quickview-body]');
    dialog.setAttribute('data-loading', '');
    if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();

    // A second click while the first is in flight must not race it.
    this.#controller?.abort();
    this.#controller = new AbortController();

    try {
      let html = this.#cache.get(url);
      if (!html) {
        const response = await fetch(url, { signal: this.#controller.signal });
        if (!response.ok) throw new Error(`Quick view ${response.status}`);
        html = await response.text();
        this.#cache.set(url, html);
      }
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('[data-nuvrya-quickview-content]') ?? doc.querySelector('.shopify-section');
      if (!content) throw new Error('Quick view payload had no content');
      body.replaceChildren(...content.childNodes);
      dialog.removeAttribute('data-loading');
      body.querySelector('a, button, input, select')?.focus({ preventScroll: true });
      announce(body.querySelector('h1, h2, [data-product-title]')?.textContent?.trim() ?? '');
    } catch (error) {
      if (error.name === 'AbortError') return;
      dialog.removeAttribute('data-loading');
      // Never leave the shopper stuck in a broken modal — fall through to the
      // real product page, which always works.
      if (trigger?.href) window.location.href = trigger.href;
      else dialog.close();
    }
  }
}
if (!customElements.get('nuvrya-quick-view')) customElements.define('nuvrya-quick-view', NuvryaQuickView);

/* =============================================================
 * Faceted filtering
 *
 * The reference did not fire a request on a bare checkbox click, so there is
 * no measured latency to copy. The brief requires AJAX + URL sync, which is
 * also the correct Shopify pattern, so that is what is built: change the
 * input, replace the results section, push the state.
 * ============================================================= */
class NuvryaFacets extends HTMLElement {
  #controller = null;
  #debounce = null;

  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.addEventListener('change', this.#onChange);
    this.addEventListener('submit', this.#onSubmit);
    window.addEventListener('popstate', this.#onPop);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit);
    window.removeEventListener('popstate', this.#onPop);
    clearTimeout(this.#debounce);
  }

  #onSubmit = (event) => {
    event.preventDefault();
    this.form = this.querySelector('form');
    this.#apply();
  };

  #onChange = (event) => {
    // Price is typed, so it debounces; everything else applies immediately.
    const isText = event.target.matches('input[type="number"], input[type="range"], input[type="text"]');
    clearTimeout(this.#debounce);
    this.#debounce = setTimeout(() => this.#apply(), isText ? 450 : 0);
  };

  #onPop = () => this.#render(window.location.search, { push: false });

  #apply() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    const data = new FormData(this.form);
    const params = new URLSearchParams();
    for (const [key, value] of data.entries()) {
      // An empty price bound is not a filter; sending it produces an ugly URL
      // and a pointless server round-trip.
      if (value === '' || value == null) continue;
      params.append(key, value);
    }
    this.#render(`?${params.toString()}`, { push: true });
  }

  async #render(search, { push }) {
    const url = `${window.location.pathname}${search}`;
    this.setAttribute('data-loading', '');
    document.documentElement.setAttribute('data-nuvrya-loading', '');

    this.#controller?.abort();
    this.#controller = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#controller.signal });
      if (!response.ok) throw new Error(`Facets ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

      for (const selector of ['[data-nuvrya-collection-results]', '[data-nuvrya-facets-form]', '[data-nuvrya-collection-count]']) {
        const next = doc.querySelector(selector);
        const current = document.querySelector(selector);
        if (next && current) current.replaceWith(next);
      }

      if (push) window.history.pushState({ nuvryaFacets: true }, '', url);

      const count = document.querySelector('[data-nuvrya-collection-count]')?.textContent?.trim();
      announce(count || 'Results updated');
    } catch (error) {
      if (error.name === 'AbortError') return;
      // A failed filter must not strand the shopper on stale results.
      window.location.href = url;
    } finally {
      this.removeAttribute('data-loading');
      document.documentElement.removeAttribute('data-nuvrya-loading');
    }
  }
}
if (!customElements.get('nuvrya-facets')) customElements.define('nuvrya-facets', NuvryaFacets);

/* =============================================================
 * Sort popover — measured as a small anchored dialog (204x332) offering all
 * nine Shopify sort modes.
 * ============================================================= */
class NuvryaSort extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-nuvrya-sort-trigger]');
    this.panel = this.querySelector('[data-nuvrya-sort-panel]');
    this.trigger?.addEventListener('click', this.#toggle);
    this.addEventListener('change', this.#onChange);
    document.addEventListener('click', this.#onOutside);
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.#close();
        this.trigger?.focus();
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onOutside);
  }

  #toggle = () => (this.hasAttribute('open') ? this.#close() : this.#open());

  #open() {
    this.setAttribute('open', '');
    this.trigger?.setAttribute('aria-expanded', 'true');
    this.panel?.querySelector('input:checked, input')?.focus({ preventScroll: true });
  }

  #close() {
    this.removeAttribute('open');
    this.trigger?.setAttribute('aria-expanded', 'false');
  }

  #onOutside = (e) => {
    if (this.hasAttribute('open') && !this.contains(e.target)) this.#close();
  };

  #onChange = (event) => {
    const value = event.target.value;
    const params = new URLSearchParams(window.location.search);
    params.set('sort_by', value);
    params.delete('page');
    const url = `${window.location.pathname}?${params.toString()}`;
    this.#close();
    // Reuse the facets pipeline so sorting and filtering share one code path.
    const facets = document.querySelector('nuvrya-facets');
    if (facets) {
      const form = facets.querySelector('form');
      let hidden = form?.querySelector('input[name="sort_by"]');
      if (form && !hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'sort_by';
        form.appendChild(hidden);
      }
      if (hidden) {
        hidden.value = value;
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        return;
      }
    }
    window.location.href = url;
  };
}
if (!customElements.get('nuvrya-sort')) customElements.define('nuvrya-sort', NuvryaSort);

/* Collection filters use the same measured left native dialog as Athora:
 * 448px wide, 30/20px outer inset, 10px inner radius, blur-15 backdrop. */
class NuvryaFilterDrawer extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-nuvrya-filter-open]');
    this.dialog = this.querySelector('[data-nuvrya-filter-dialog]');
    this.surface = this.querySelector('[data-nuvrya-filter-surface]');
    if (!this.trigger || !this.dialog || !this.surface) return;
    this.trigger.addEventListener('click', this.#open);
    this.querySelectorAll('[data-nuvrya-filter-close]').forEach((button) => button.addEventListener('click', this.#close));
    this.dialog.addEventListener('cancel', this.#onCancel);
    this.dialog.addEventListener('click', this.#onBackdrop);
    this.dialog.addEventListener('close', this.#cleanup);
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.#open);
    this.dialog?.removeEventListener('cancel', this.#onCancel);
    this.dialog?.removeEventListener('click', this.#onBackdrop);
    this.dialog?.removeEventListener('close', this.#cleanup);
  }

  #open = () => {
    if (this.dialog.open) return;
    this.dialog.showModal();
    this.trigger.setAttribute('aria-expanded', 'true');
    const duration = RM.matches ? 1 : 360;
    this.surface.animate(
      [{ opacity: 0, transform: 'translateX(-28px)' }, { opacity: 1, transform: 'translateX(0)' }],
      { duration, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    );
    this.querySelector('[data-nuvrya-filter-close]')?.focus({ preventScroll: true });
  };

  #close = () => {
    if (this.dialog.open) this.dialog.close();
  };

  #onCancel = (event) => {
    event.preventDefault();
    this.#close();
  };

  #onBackdrop = (event) => {
    if (event.target === this.dialog) this.#close();
  };

  #cleanup = () => {
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.focus({ preventScroll: true });
  };
}
if (!customElements.get('nuvrya-filter-drawer')) customElements.define('nuvrya-filter-drawer', NuvryaFilterDrawer);

/* =============================================================
 * Variant switching
 *
 * Measured: switching a variant on the reference fires **no network request**
 * — zero fetch, zero XHR — and only updates the URL to `?variant=<id>` plus a
 * couple of DOM mutations. The brief's "section-level AJAX refresh" was wrong.
 * Reading the embedded variant JSON is both faster and what the reference does.
 * ============================================================= */
class NuvryaVariants extends HTMLElement {
  connectedCallback() {
    this.productUrl = this.getAttribute('data-product-url');
    try {
      this.variants = JSON.parse(this.querySelector('[data-nuvrya-variant-data]')?.textContent || '[]');
    } catch {
      this.variants = [];
    }
    this.addEventListener('change', this.#onChange);
  }

  get selectedOptions() {
    return Array.from(this.querySelectorAll('input[type="radio"]:checked, select')).map((i) => i.value);
  }

  #onChange = () => {
    const chosen = this.selectedOptions;
    const variant = this.variants.find((v) => v.options.every((opt, i) => opt === chosen[i]));
    this.#update(variant);
  };

  #update(variant) {
    this.querySelectorAll('[data-nuvrya-variant-unavailable]').forEach((el) => el.toggleAttribute('hidden', !!variant));

    if (!variant) {
      this.#setButton({ available: false, text: this.getAttribute('data-text-unavailable') || 'Unavailable' });
      return;
    }

    // URL sync without a navigation — replaceState keeps the back button useful.
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    this.querySelectorAll('[data-nuvrya-variant-id]').forEach((input) => {
      input.value = variant.id;
    });

    const price = this.closest('[data-nuvrya-product]')?.querySelector('[data-nuvrya-price]');
    if (price && variant.price_html) price.innerHTML = variant.price_html;

    const sku = this.closest('[data-nuvrya-product]')?.querySelector('[data-nuvrya-sku]');
    if (sku) sku.textContent = variant.sku || '';

    this.#setButton({
      available: variant.available,
      text: variant.available
        ? this.getAttribute('data-text-add') || 'Add to cart'
        : this.getAttribute('data-text-soldout') || 'Sold out',
    });

    this.#setInventory(variant);
    this.#setMedia(variant);

    this.dispatchEvent(new CustomEvent('nuvrya:variant:change', { bubbles: true, detail: { variant } }));
    announce(`${variant.title}. ${variant.available ? 'In stock' : 'Sold out'}`);
  }

  #setButton({ available, text }) {
    this.closest('[data-nuvrya-product]')
      ?.querySelectorAll('[data-nuvrya-add-to-cart]')
      .forEach((button) => {
        button.disabled = !available;
        const label = button.querySelector('[data-nuvrya-add-to-cart-text]') || button;
        label.textContent = text;
      });
  }

  #setInventory(variant) {
    const el = this.closest('[data-nuvrya-product]')?.querySelector('[data-nuvrya-inventory]');
    if (!el) return;
    const qty = variant.inventory_quantity;
    const threshold = Number(el.getAttribute('data-threshold') || 10);
    const show = variant.available && Number.isFinite(qty) && qty > 0 && qty <= threshold;
    el.toggleAttribute('hidden', !show);
    if (show) {
      el.querySelector('[data-nuvrya-inventory-count]')?.replaceChildren(String(qty));
      el.style.setProperty('--nuvrya-inventory-ratio', String(Math.min(1, qty / threshold)));
    }
  }

  #setMedia(variant) {
    if (!variant.featured_media_id) return;
    const gallery = this.closest('[data-nuvrya-product]')?.querySelector('[data-nuvrya-gallery]');
    gallery
      ?.querySelector(`[data-media-id="${variant.featured_media_id}"]`)
      ?.scrollIntoView({ behavior: RM.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
  }
}
if (!customElements.get('nuvrya-variants')) customElements.define('nuvrya-variants', NuvryaVariants);

/* =============================================================
 * Sticky add to cart
 *
 * Appears only once the real purchase module has scrolled past, with an
 * animated entrance. IntersectionObserver rather than a scroll handler so the
 * main thread stays free.
 * ============================================================= */
class NuvryaStickyAtc extends HTMLElement {
  connectedCallback() {
    const anchorSelector = this.getAttribute('data-nuvrya-anchor') || '[data-nuvrya-buy-buttons]';
    const anchor = document.querySelector(anchorSelector);
    if (!anchor) return;
    this.#io = new IntersectionObserver(
      ([entry]) => {
        // Visible only when the anchor has left upward: still below the fold
        // must not trigger it.
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.toggleAttribute('data-visible', passed);
        this.toggleAttribute('inert', !passed);
      },
      { threshold: 0, rootMargin: '0px' }
    );
    this.#io.observe(anchor);
    this.toggleAttribute('inert', true);
  }

  disconnectedCallback() {
    this.#io?.disconnect();
  }

  #io = null;
}
if (!customElements.get('nuvrya-sticky-atc')) customElements.define('nuvrya-sticky-atc', NuvryaStickyAtc);

/* =============================================================
 * Before / after
 *
 * Measured: the reference writes `--percent-val: <n>%` onto the host element
 * and updates it continuously through pointer and touch drags. Same mechanism
 * here, plus keyboard support the reference lacks.
 * ============================================================= */
class NuvryaBeforeAfter extends HTMLElement {
  #dragging = false;

  connectedCallback() {
    this.handle = this.querySelector('[data-nuvrya-ba-handle]');
    this.#set(Number(this.getAttribute('data-start') || 50));

    this.addEventListener('pointerdown', this.#down);
    this.addEventListener('pointermove', this.#move);
    this.addEventListener('pointerup', this.#up);
    this.addEventListener('pointercancel', this.#up);
    // Touch scrolling must still work vertically, so only horizontal panning
    // is claimed by this element.
    this.style.touchAction = 'pan-y';
    this.handle?.addEventListener('keydown', this.#key);
  }

  #percentFromEvent(event) {
    const rect = this.getBoundingClientRect();
    const raw = ((event.clientX - rect.left) / rect.width) * 100;
    const rtl = getComputedStyle(this).direction === 'rtl';
    return Math.min(100, Math.max(0, rtl ? 100 - raw : raw));
  }

  #down = (event) => {
    if (event.button != null && event.button !== 0) return;
    this.#dragging = true;
    this.setPointerCapture?.(event.pointerId);
    this.#set(this.#percentFromEvent(event));
  };

  #move = (event) => {
    if (!this.#dragging) return;
    // Continuous update during the drag — this is the acceptance criterion.
    this.#set(this.#percentFromEvent(event));
  };

  #up = (event) => {
    this.#dragging = false;
    this.releasePointerCapture?.(event.pointerId);
  };

  #key = (event) => {
    const step = event.shiftKey ? 10 : 2;
    const current = Number(this.dataset.percent || 50);
    if (event.key === 'ArrowLeft') this.#set(current - step);
    else if (event.key === 'ArrowRight') this.#set(current + step);
    else if (event.key === 'Home') this.#set(0);
    else if (event.key === 'End') this.#set(100);
    else return;
    event.preventDefault();
  };

  #set(percent) {
    const value = Math.min(100, Math.max(0, percent));
    this.dataset.percent = String(value);
    this.style.setProperty('--percent-val', `${value}%`);
    this.handle?.setAttribute('aria-valuenow', String(Math.round(value)));
  }
}
if (!customElements.get('nuvrya-before-after')) customElements.define('nuvrya-before-after', NuvryaBeforeAfter);

/* =============================================================
 * Lightbox previous / next
 *
 * Horizon's zoom-dialog already provides full-screen, zoom, drag, pinch and
 * double-tap, and its sources reach 3840px — above the 2048px the reference
 * serves. The only observable it lacked was discrete previous/next controls,
 * so those are added here rather than shipping a second lightbox library.
 * ============================================================= */
class NuvryaZoomNav extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onKey);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKey);
  }

  get #dialogHost() {
    return this.closest('zoom-dialog') || document.querySelector('zoom-dialog');
  }

  #step(delta) {
    const host = this.#dialogHost;
    const slides = host?.querySelectorAll('.dialog-zoomed-gallery > li');
    if (!slides?.length) return;
    const viewportMiddle = window.innerHeight / 2;
    let activeIndex = 0;
    let best = Infinity;
    slides.forEach((slide, i) => {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.top + rect.height / 2 - viewportMiddle);
      if (distance < best) {
        best = distance;
        activeIndex = i;
      }
    });
    const next = Math.min(slides.length - 1, Math.max(0, activeIndex + delta));
    slides[next]?.scrollIntoView({ behavior: RM.matches ? 'auto' : 'smooth', block: 'center' });
    announce(`Image ${next + 1} of ${slides.length}`);
  }

  #onClick = (event) => {
    if (event.target.closest('[data-nuvrya-zoom-prev]')) this.#step(-1);
    if (event.target.closest('[data-nuvrya-zoom-next]')) this.#step(1);
  };

  #onKey = (event) => {
    const dialog = this.#dialogHost?.querySelector('dialog[open]');
    if (!dialog) return;
    if (event.key === 'ArrowLeft') this.#step(-1);
    if (event.key === 'ArrowRight') this.#step(1);
  };
}
if (!customElements.get('nuvrya-zoom-nav')) customElements.define('nuvrya-zoom-nav', NuvryaZoomNav);

/* =============================================================
 * Free shipping progress — reads the cart total from Horizon's cart events so
 * it stays correct after every add, remove and quantity change.
 * ============================================================= */
class NuvryaShippingProgress extends HTMLElement {
  connectedCallback() {
    this.threshold = Number(this.getAttribute('data-threshold') || 0);
    this.#render(Number(this.getAttribute('data-total') || 0));
    document.addEventListener('cart:update', this.#onCart);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.#onCart);
  }

  #onCart = (event) => {
    const total = event?.detail?.data?.total_price ?? event?.detail?.total_price;
    if (Number.isFinite(total)) this.#render(total);
  };

  #render(total) {
    if (!this.threshold) return;
    const ratio = Math.min(1, total / this.threshold);
    this.style.setProperty('--nuvrya-progress', String(ratio));
    this.toggleAttribute('data-complete', ratio >= 1);
    const bar = this.querySelector('[role="progressbar"]');
    bar?.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
  }
}
if (!customElements.get('nuvrya-shipping-progress')) customElements.define('nuvrya-shipping-progress', NuvryaShippingProgress);

/* Optional cart add-ons are real products selected by the merchant. Nothing is
 * fabricated: when no gift-wrap/protection product is configured, no toggle is
 * rendered. Mutations request the drawer section in the same round trip. */
class NuvryaCartAddon extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('input[type="checkbox"]');
    this.checkbox?.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.checkbox?.removeEventListener('change', this.#onChange);
  }

  #onChange = async () => {
    if (!this.checkbox || this.hasAttribute('data-pending')) return;
    const checked = this.checkbox.checked;
    const variantId = Number(this.dataset.variantId || 0);
    const lineKey = this.dataset.lineKey;
    if (!variantId) return;

    this.setAttribute('data-pending', '');
    this.checkbox.disabled = true;
    const root = window.Shopify?.routes?.root || '/';
    const endpoint = checked ? `${root}cart/add.js` : `${root}cart/change.js`;
    const body = checked
      ? { items: [{ id: variantId, quantity: 1 }] }
      : { id: lineKey || variantId, quantity: 0 };
    body.sections = ['cart-drawer-section'];
    body.sections_url = window.location.pathname;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || `Cart update ${response.status}`);

      const sectionHtml = payload.sections?.['cart-drawer-section'];
      if (sectionHtml) {
        const documentFragment = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const next = documentFragment.querySelector('cart-drawer-component');
        const current = document.querySelector('#cart-drawer cart-drawer-component');
        if (next && current) current.replaceWith(next);
      }

      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      document.dispatchEvent(new CustomEvent('cart:update', { detail: { data: cart } }));
      announce(checked ? 'Cart add-on added.' : 'Cart add-on removed.');
    } catch (error) {
      this.checkbox.checked = !checked;
      this.checkbox.disabled = false;
      this.removeAttribute('data-pending');
      announce(error instanceof Error ? error.message : 'Cart update failed.');
    }
  };
}
if (!customElements.get('nuvrya-cart-addon')) customElements.define('nuvrya-cart-addon', NuvryaCartAddon);

/* Product contact/share actions. The question form is a native modal so focus
 * stays trapped by the browser; Web Share is used when available and falls
 * back to the clipboard without navigating away from the PDP. */
class NuvryaProductActions extends HTMLElement {
  connectedCallback() {
    this.openButton = this.querySelector('[data-nuvrya-question-open]');
    this.dialog = this.querySelector('[data-nuvrya-question-dialog]');
    this.closeButton = this.querySelector('[data-nuvrya-question-close]');
    this.shareButton = this.querySelector('[data-nuvrya-share]');
    this.openButton?.addEventListener('click', this.#open);
    this.closeButton?.addEventListener('click', this.#close);
    this.dialog?.addEventListener('cancel', this.#onCancel);
    this.dialog?.addEventListener('click', this.#onBackdrop);
    this.dialog?.addEventListener('close', this.#restoreFocus);
    this.shareButton?.addEventListener('click', this.#share);
  }

  disconnectedCallback() {
    this.openButton?.removeEventListener('click', this.#open);
    this.closeButton?.removeEventListener('click', this.#close);
    this.dialog?.removeEventListener('cancel', this.#onCancel);
    this.dialog?.removeEventListener('click', this.#onBackdrop);
    this.dialog?.removeEventListener('close', this.#restoreFocus);
    this.shareButton?.removeEventListener('click', this.#share);
  }

  #open = () => {
    if (!this.dialog?.open) this.dialog?.showModal();
    this.closeButton?.focus({ preventScroll: true });
  };

  #close = () => this.dialog?.close();

  #onCancel = (event) => {
    event.preventDefault();
    this.#close();
  };

  #onBackdrop = (event) => {
    if (event.target === this.dialog) this.#close();
  };

  #restoreFocus = () => this.openButton?.focus({ preventScroll: true });

  #share = async () => {
    const payload = { title: document.title, url: window.location.href };
    try {
      if (navigator.share) await navigator.share(payload);
      else await navigator.clipboard.writeText(payload.url);
      const status = this.querySelector('[data-nuvrya-share-status]');
      if (status) status.textContent = navigator.share ? 'Share sheet opened.' : 'Product link copied.';
    } catch (error) {
      if (error?.name !== 'AbortError') announce('Unable to share this product.');
    }
  };
}
if (!customElements.get('nuvrya-product-actions')) customElements.define('nuvrya-product-actions', NuvryaProductActions);

/* Homepage product configurator. The same component runs in read-only draft
 * preview and live product-form modes; cart submission only exists in Liquid
 * when a real variant has an approved non-zero price and is available. */
class NuvryaFeaturedProduct extends HTMLElement {
  connectedCallback() {
    this.frames = [...this.querySelectorAll('[data-featured-frame]')];
    this.thumbs = [...this.querySelectorAll('[data-featured-thumb]')];
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    const variantsNode = this.querySelector('[data-featured-variants]');
    if (variantsNode) {
      try {
        this.variants = JSON.parse(variantsNode.textContent);
      } catch {
        this.variants = [];
      }
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
  }

  #onClick = (event) => {
    const thumb = event.target.closest('[data-featured-thumb]');
    if (thumb) {
      const index = Number(thumb.dataset.featuredThumb || 0);
      this.frames.forEach((frame, frameIndex) => frame.toggleAttribute('hidden', frameIndex !== index));
      this.thumbs.forEach((item, itemIndex) => item.setAttribute('aria-pressed', String(itemIndex === index)));
      return;
    }

    const previewOption = event.target.closest('[data-featured-preview-option]');
    if (previewOption) {
      const group = previewOption.parentElement;
      group?.querySelectorAll('[data-featured-preview-option]').forEach((item) => {
        item.setAttribute('aria-pressed', String(item === previewOption));
      });
      return;
    }

    const quantity = this.querySelector('input[name="quantity"]');
    if (!quantity) return;
    if (event.target.closest('[data-featured-quantity-minus]')) quantity.value = String(Math.max(1, Number(quantity.value || 1) - 1));
    if (event.target.closest('[data-featured-quantity-plus]')) quantity.value = String(Number(quantity.value || 1) + 1);
  };

  #onChange = (event) => {
    if (!event.target.matches('[data-featured-option] input[type="radio"]') || !this.variants?.length) return;
    const selected = [...this.querySelectorAll('[data-featured-option]')].map((group) => group.querySelector('input:checked')?.value);
    const variant = this.variants.find((candidate) => candidate.options.every((value, index) => value === selected[index]));
    const variantInput = this.querySelector('[data-featured-variant-id]');
    const submit = this.querySelector('[data-featured-form] button[type="submit"]');
    if (variantInput && variant) variantInput.value = variant.id;
    if (submit) submit.disabled = !variant?.available;
    if (variant) window.history.replaceState({}, '', `${window.location.pathname}?variant=${variant.id}`);
  };
}
if (!customElements.get('nuvrya-featured-product')) customElements.define('nuvrya-featured-product', NuvryaFeaturedProduct);

export {
  NuvryaQuickView,
  NuvryaFacets,
  NuvryaSort,
  NuvryaFilterDrawer,
  NuvryaVariants,
  NuvryaStickyAtc,
  NuvryaBeforeAfter,
  NuvryaZoomNav,
  NuvryaShippingProgress,
  NuvryaCartAddon,
  NuvryaProductActions,
  NuvryaFeaturedProduct,
};
